import React from 'react'
import { LuArrowRight } from 'react-icons/lu'

import CompanyAvatar from '@/components/misc/CompanyAvatar'
import SearchResultItem from '@/components/misc/GlobalSearch/components/SearchResultItem'
import { SearchResult } from '@/components/misc/GlobalSearch/types'
import { CommandGroup } from '@/components/ui/command'
import { useAuth } from '@/hooks/useAuth'
import { useUserCompanies } from '@/hooks/useUserCompanies'
import globalSearchService from '@/lib/services/globalSearchService'

interface Props {
  companies: SearchResult[]
  /** Starting pop-in index, so these animate in after the other results. */
  indexOffset?: number
}

/**
 * The subset of company results that the current user is a member of, offering
 * a quick way to switch into that company rather than view its profile.
 */
const UserCompanyResults: React.FC<Props> = ({
  companies,
  indexOffset = 0,
}) => {
  const userCompanies = useUserCompanies()
  const { updateCompanyContext } = useAuth()

  // Pair each search result with the matching company from the user's session,
  // whose IRI is needed to switch context. Only registered (on-RQ) companies
  // can be ones the user is a member of.
  const ownCompanies = companies.flatMap((company) => {
    if (!globalSearchService.isRegisteredCompany(company)) {
      return []
    }

    const userCompany = userCompanies.find(
      (userCompany) => userCompany.id === company.id,
    )

    return userCompany ? [{ company, userCompany }] : []
  })

  if (ownCompanies.length === 0) {
    return null
  }

  return (
    <CommandGroup heading="Your companies">
      {ownCompanies.map(({ company, userCompany }, index) => (
        <SearchResultItem
          key={company['@id']}
          index={indexOffset + index}
          value={`switch-${company['@id']}`}
          className="gap-3"
          onSelect={() => updateCompanyContext({ company: userCompany['@id'] })}
        >
          <CompanyAvatar
            size="lg"
            tooltip={false}
            company={{
              presentationName: company.presentationName,
              iconUrl: company.iconUrl,
            }}
          />

          <div className="flex flex-1 min-w-0 flex-col">
            <span className="truncate font-medium">
              {company.presentationName}
            </span>

            {company.companyType?.name && (
              <span className="text-muted-foreground text-xs">
                {company.companyType.name}
              </span>
            )}
          </div>

          <div className="flex items-center">
            <span
              className="flex flex-nowrap items-center text-[10px] gap-1
                bg-blue-100 p-1 ps-2 rounded-full text-blue-900"
            >
              Switch <LuArrowRight className="text-blue-900 size-3" />
            </span>
          </div>
        </SearchResultItem>
      ))}
    </CommandGroup>
  )
}

export default UserCompanyResults
